import { ContactService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Contact } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Button, Container, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { MatchParam } from '../../../common/types';
import { format } from '../../../common/util';
import { ContactManagementContext } from '../../../contexts/ContactManagementContext';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import ConfirmationModal from '../../common/ConfirmationModal';
import PreviewBanner from '../../common/PreviewBanner';
import { ContactActions, getContactsTableColumnDefinitions } from './contact-management-helper';
import ContactActionModal from './ContactActionModal';
import ContactManagementOwnership from './ContactManagementOwnership';

type Props = FlashContextInterface;

const contactService = new ContactService();

function ContactManagement({ flash }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;
    const isMounted = useIsMounted();
    const { panelHandler } = useContext(HelpPanelContext);
    
    const [ loadingContacts, setLoadingContacts ] = useState(false);
    const [ contacts, setContacts ] = useState<Contact[]>([]);
    const [ selectedContacts, setSelectedContacts ] = useState<Contact[]>([]);
    const [ actionModalVisible, setActionModalVisible ] = useState(false);
    const [ actionModalAction, setActionModalAction ] = useState<ContactActions | null>();
    const [ deleteModalVisible, setDeleteModalVisible ] = useState(false);
    const { applications, servers, databases } = useContext(ContactManagementContext);

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.CONTACT_MANAGEMENT);
    }, [ panelHandler ]);
    
    const loadContact = async () => {
        if (!portfolioId || !isMounted()) {
            return;
        }

        setLoadingContacts(true);
        try {
            const contacts = await contactService.list(portfolioId);
            if (isMounted()) {
                setContacts(contacts);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setLoadingContacts(false);
            }
        }
    };

    useEffect(() => {
        loadContact();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioId]);

    const performActionOnContact = (action: ContactActions) => () => {
        setActionModalAction(action);
        setActionModalVisible(true);
    };

    const contactActions = (
        <SpaceBetween size='m' direction='horizontal'>
            <Button variant='primary' onClick={performActionOnContact('add')}>{constants.ADD_CONTACT}</Button>
            <Button href={`/portfolio/${portfolioId}/management/data-collection`}>{constants.START_DATA_REQUEST}</Button>
        </SpaceBetween>
    );

    const contactTableHeaderActions = (
        <SpaceBetween size='m' direction='horizontal'>
            <Button disabled={selectedContacts.length === 0} onClick={performActionOnContact('edit')}>{constants.EDIT_BUTTON_TEXT}</Button>
            <Button disabled={selectedContacts.length === 0} onClick={() => setDeleteModalVisible(true)}>{constants.DELETE_BUTTON_TEXT}</Button>
        </SpaceBetween>
    );

    const deleteWarning = (serverCount: number, applicationCount: number, databaseCount: number): JSX.Element => 
        <Box>
            <span>{format(constants.THIS_CONTACT_WILL_BE_UNASSIGNED, 
                [serverCount.toString(), applicationCount.toString(), databaseCount.toString()])}</span>
        </Box>;

    const deleteContent = (contact: Contact): JSX.Element => {
        const applicationCount = applications.filter(x => x.businessOwnerId === contact?.contactId).length;
        const databaseCount = databases.filter(x => x.dbOwnerId === contact?.contactId).length;
        const serverCount = servers.filter(x=> x.serverSupportId === contact?.contactId).length;
        return (
            <Box>
                <SpaceBetween size='xxs'>
                    <Box>{constants.DELETE_CONTACT_CONFIRM}</Box>
                    <Box><strong>{constants.NAME} -</strong> {contact?.name}</Box>
                    <Box><strong>{constants.EMAIL} -</strong> {contact?.email}</Box>
                    {contact?.phone ? <Box><strong>{constants.PHONE} -</strong> {contact.phone}</Box> : null}
                    { serverCount + applicationCount + databaseCount > 0 ? deleteWarning(serverCount, applicationCount, databaseCount) : ''}
                </SpaceBetween>
            </Box>
        );
    };

    const getContactsTable = (): JSX.Element => {
        if (loadingContacts) {
            return (
                <Spinner size='large'/>
            );
        }
        if (contacts.length === 0) {
            return (
                <Box padding={{top: 'm', bottom: 'm'}}>
                    <Alert
                        header={constants.ADD_CONTACT}
                    >
                        {constants.NO_CONTACT_DATA} {constants.CLICK_THE} <strong>{constants.ADD_CONTACT}</strong> {constants.BUTTON_TO_ADD_CONTACTS}
                    </Alert>
                </Box>
            );
        } else {
            return (
                <AWSCATTable
                    resourceName={constants.CONTACTS}
                    items={contacts}
                    columnDefinitions={getContactsTableColumnDefinitions}
                    headerActions={contactTableHeaderActions}
                    selectionType='single'
                    onSelectionChange={setSelectedContacts as () => void}
                    autoGenerateVisibleContent
                />
            );
        }
    };

    const deleteContact = async (): Promise<void> => {
        if (!portfolioId){
            return;
        }

        try {
            await contactService.delete(portfolioId, selectedContacts[0].contactId);
        } catch (err) {
            if (isMounted()){
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setDeleteModalVisible(false);
            }
            loadContact();
        }
    };
    
    return (
        <Container>
            <SpaceBetween size='m'>
                <PreviewBanner />
                {contactActions}
                {getContactsTable()}
                <ContactManagementOwnership
                    contacts={contacts}
                />
                {deleteModalVisible ? (
                    <ConfirmationModal
                        header={constants.DELETE_CONTACT}
                        onConfirm={deleteContact}
                        onDismiss={() => setDeleteModalVisible(false)}
                        onCancel={() => setDeleteModalVisible(false)}
                        visible
                        friction='delete'
                    >
                        {deleteContent(selectedContacts[0])}
                    </ConfirmationModal>
                ) : null
                }
                {actionModalVisible && actionModalAction ? (
                    <ContactActionModal
                        action={actionModalAction}
                        contact={selectedContacts[0]}
                        contacts={contacts}
                        visible={actionModalVisible}
                        setVisible={setActionModalVisible}
                        onConfirmChange={() => {loadContact();}}
                    />
                ) : null
                }
            </SpaceBetween>
        </Container>
    );
}

export default withFlashContext(ContactManagement);
